import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { API } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  ArrowLeft,
  CheckCircle2,
  ChefHat,
  Clock,
  Delete,
  LogIn,
  LogOut,
  ShieldCheck,
  Truck,
  User2,
  UserCog,
  UtensilsCrossed,
} from 'lucide-react';
import { KioskLockBar } from '@/lib/kioskLock';

const ROLE_ICON = {
  kitchen: ChefHat,
  cashier: UserCog,
  driver: Truck,
  admin: ShieldCheck,
  owner: ShieldCheck,
};
const ROLE_TINT = {
  kitchen: 'bg-amber-500/20 text-amber-200',
  cashier: 'bg-sky-500/20 text-sky-200',
  driver: 'bg-emerald-500/20 text-emerald-200',
  admin: 'bg-violet-500/20 text-violet-200',
  owner: 'bg-rose-500/20 text-rose-200',
};

function initials(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase() || '?';
}

/**
 * City Grill staff entrance kiosk.
 * Flow:
 *  1. Tap a staff card (no auth needed — staff are listed by /staff/kiosk/list)
 *  2. Enter 4–6 digit PIN on the on-screen keypad
 *  3. Choose Clock in or Clock out
 *  4. Confirmation auto-resets after 4 seconds
 * No JWT is ever stored on this device.
 */
export default function CheckinKiosk() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null); // staff object
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [now, setNow] = useState(new Date());

  // Live clock on the header
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000 * 30);
    return () => clearInterval(t);
  }, []);

  const loadMembers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/staff/kiosk/list`);
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || 'Failed');
      setMembers(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error('Unable to load staff list');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { loadMembers(); }, [loadMembers]);

  const reset = () => { setSelected(null); setPin(''); setResult(null); };

  const submit = async (action) => {
    if (!selected) return;
    if (!/^\d{5}$/.test(pin)) { toast.error('Enter your 5-digit PIN'); return; }
    setBusy(true);
    try {
      const res = await fetch(`${API}/staff/kiosk/${action}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: selected.id, pin }),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data?.detail || 'Failed');
        setPin('');
        setBusy(false);
        return;
      }
      setResult({ action, ...data });
      setTimeout(reset, 4500);
    } catch (e) {
      toast.error('Network error');
    } finally {
      setBusy(false);
    }
  };

  const visibleMembers = useMemo(() => members, [members]);

  return (
    <div className="min-h-screen bg-[#0f1115] text-white" data-testid="checkin-kiosk-page">
      <KioskLockBar title="Check-in" position="top-right" />
      {/* Header */}
      <header className="flex items-center justify-between px-6 py-4 border-b border-white/10">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-full bg-primary">
            <UtensilsCrossed className="h-6 w-6 text-white" />
          </span>
          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] opacity-60">Staff entrance</p>
            <h1 className="font-display text-2xl font-semibold">City Grill Kiosk</h1>
          </div>
        </div>
        <div className="text-right">
          <p className="font-display text-2xl tabular">{now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>
          <p className="text-[11px] uppercase tracking-wider opacity-60">{now.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' })}</p>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-6 py-8">
        {/* Success/result view */}
        {result && (
          <Card className="mx-auto max-w-xl border-white/15 bg-white/5 text-white">
            <CardContent className="flex flex-col items-center gap-3 p-10 text-center">
              <span className={`flex h-20 w-20 items-center justify-center rounded-full ${result.action === 'clock-out' ? 'bg-amber-500/30' : 'bg-emerald-500/30'}`}>
                {result.action === 'clock-out' ? <Clock className="h-10 w-10 text-amber-200" /> : <CheckCircle2 className="h-10 w-10 text-emerald-200" />}
              </span>
              <h2 className="font-display text-3xl font-semibold">{result.user_name}</h2>
              <p className="text-xs uppercase tracking-[0.25em] opacity-70">{result.role}</p>
              <p className="font-display text-2xl text-primary mt-2">
                {result.action === 'clock-out'
                  ? `Clocked out · ${result.duration_minutes} min on shift`
                  : result.action === 'clock-in' && result.action !== 'already_open'
                  ? 'Clocked in — have a great shift'
                  : 'Already on shift'}
              </p>
              <Button data-testid="kiosk-done-button" size="lg" variant="secondary" className="mt-4" onClick={reset}>Done</Button>
            </CardContent>
          </Card>
        )}

        {/* PIN entry view */}
        {!result && selected && (
          <Card className="mx-auto max-w-md border-white/15 bg-white/5 text-white">
            <CardContent className="space-y-5 p-6">
              <div className="flex items-center gap-3">
                <Button data-testid="kiosk-back-button" size="icon" variant="ghost" className="text-white hover:bg-white/10" onClick={reset}>
                  <ArrowLeft className="h-5 w-5" />
                </Button>
                <div className="flex flex-1 items-center gap-3">
                  <span className={`flex h-12 w-12 items-center justify-center rounded-full ${ROLE_TINT[selected.role] || 'bg-white/10'} font-semibold`}>
                    {initials(selected.full_name)}
                  </span>
                  <div>
                    <p className="font-display text-xl">{selected.full_name}</p>
                    <p className="text-[11px] uppercase tracking-wider opacity-60">{selected.role}</p>
                  </div>
                </div>
              </div>

              {/* PIN dots (exactly 5) */}
              <div className="flex items-center justify-center gap-3 py-2">
                {[0, 1, 2, 3, 4].map((i) => (
                  <span
                    key={i}
                    className={`h-3.5 w-3.5 rounded-full transition ${i < pin.length ? 'bg-primary' : 'bg-white/15'}`}
                  />
                ))}
              </div>

              {/* Keypad */}
              <div className="grid grid-cols-3 gap-3">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
                  <Button
                    key={n}
                    data-testid={`kiosk-keypad-${n}`}
                    size="lg"
                    variant="secondary"
                    className="h-16 bg-white/10 text-2xl text-white hover:bg-white/20 font-display"
                    disabled={busy || pin.length >= 5}
                    onClick={() => setPin((p) => (p.length < 5 ? p + String(n) : p))}
                  >
                    {n}
                  </Button>
                ))}
                <Button
                  data-testid="kiosk-keypad-clear"
                  size="lg"
                  variant="secondary"
                  className="h-16 bg-white/5 text-sm text-white hover:bg-white/15"
                  disabled={busy}
                  onClick={() => setPin('')}
                >
                  Clear
                </Button>
                <Button
                  data-testid="kiosk-keypad-0"
                  size="lg"
                  variant="secondary"
                  className="h-16 bg-white/10 text-2xl text-white hover:bg-white/20 font-display"
                  disabled={busy || pin.length >= 5}
                  onClick={() => setPin((p) => (p.length < 5 ? p + '0' : p))}
                >
                  0
                </Button>
                <Button
                  data-testid="kiosk-keypad-backspace"
                  size="lg"
                  variant="secondary"
                  className="h-16 bg-white/5 text-white hover:bg-white/15"
                  disabled={busy}
                  onClick={() => setPin((p) => p.slice(0, -1))}
                >
                  <Delete className="h-5 w-5" />
                </Button>
              </div>

              {/* Action — only the relevant one is shown.
                  Reads the `clocked_in` flag the backend now returns on
                  /kiosk/list so we don't surprise people with the wrong
                  verb (no "Clock in" if they're already on shift). */}
              <div className="pt-1">
                {selected.clocked_in ? (
                  <Button
                    data-testid="kiosk-clock-out-button"
                    disabled={busy || pin.length !== 5}
                    onClick={() => submit('clock-out')}
                    className="h-14 w-full bg-amber-600 text-white hover:bg-amber-700 text-base"
                  >
                    <LogOut className="mr-2 h-5 w-5" /> Tap to clock out
                  </Button>
                ) : (
                  <Button
                    data-testid="kiosk-clock-in-button"
                    disabled={busy || pin.length !== 5}
                    onClick={() => submit('clock-in')}
                    className="h-14 w-full bg-emerald-600 text-white hover:bg-emerald-700 text-base"
                  >
                    <LogIn className="mr-2 h-5 w-5" /> Tap to clock in
                  </Button>
                )}
                {selected.clocked_in && selected.clock_in && (
                  <p className="mt-2 text-center text-xs opacity-70">
                    On shift since {(() => { try { return new Date(selected.clock_in).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }); } catch (e) { return '—'; } })()}
                  </p>
                )}
              </div>

              <p className="flex items-center justify-center gap-2 text-xs opacity-60">
                <ShieldCheck className="h-3.5 w-3.5" /> Sessions are not stored on this device
              </p>
            </CardContent>
          </Card>
        )}

        {/* Staff list (tap-to-select) */}
        {!result && !selected && (
          <>
            <div className="mb-4 flex items-center justify-between">
              <div>
                <p className="font-display text-2xl">Who&rsquo;s checking in?</p>
                <p className="text-sm opacity-60">Tap your name to continue.</p>
              </div>
              <Button data-testid="kiosk-refresh-button" variant="secondary" className="bg-white/10 text-white hover:bg-white/20" onClick={loadMembers} disabled={loading}>
                {loading ? 'Loading…' : 'Refresh'}
              </Button>
            </div>

            {loading && (
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {[0, 1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="h-24 animate-pulse rounded-xl bg-white/5" />
                ))}
              </div>
            )}

            {!loading && !visibleMembers.length && (
              <Card className="border-white/15 bg-white/5 text-center text-white">
                <CardContent className="p-10">
                  <User2 className="mx-auto mb-3 h-10 w-10 opacity-60" />
                  <p className="font-display text-xl">No staff configured yet</p>
                  <p className="mt-1 text-sm opacity-60">Ask an admin to add staff members and assign a kiosk PIN.</p>
                </CardContent>
              </Card>
            )}

            {!loading && visibleMembers.length > 0 && (
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="kiosk-staff-grid">
                {visibleMembers.map((m) => {
                  const Icon = ROLE_ICON[m.role] || User2;
                  const shiftType = (m.shift_today || '').toLowerCase();
                  const unavailable = ['sick', 'pto', 'off'].includes(shiftType);
                  const disabled = !m.has_pin || (unavailable && !m.clocked_in);
                  const shiftBadge = (() => {
                    if (shiftType === 'sick') return { label: 'SICK', cls: 'border-rose-400/40 bg-rose-400/15 text-rose-200' };
                    if (shiftType === 'pto') return { label: 'PTO', cls: 'border-violet-400/40 bg-violet-400/15 text-violet-200' };
                    if (shiftType === 'off') return { label: 'OFF', cls: 'border-slate-400/40 bg-slate-400/15 text-slate-200' };
                    return null;
                  })();
                  return (
                    <button
                      key={m.id}
                      data-testid="kiosk-staff-card"
                      disabled={disabled}
                      onClick={() => { setSelected(m); setPin(''); }}
                      className={`group flex items-center gap-3 rounded-xl border border-white/10 bg-white/5 p-4 text-left transition hover:border-primary hover:bg-white/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary ${disabled ? 'cursor-not-allowed opacity-40 grayscale hover:border-white/10 hover:bg-white/5' : ''}`}
                    >
                      <span className={`flex h-12 w-12 items-center justify-center rounded-full ${ROLE_TINT[m.role] || 'bg-white/10'} font-semibold`}>
                        {initials(m.full_name)}
                      </span>
                      <div className="flex-1">
                        <p className="font-display text-lg leading-tight">{m.full_name}</p>
                        <p className="flex items-center gap-1 text-[11px] uppercase tracking-wider opacity-70">
                          <Icon className="h-3 w-3" /> {m.role}
                        </p>
                      </div>
                      {shiftBadge ? (
                        <Badge data-testid={`kiosk-staff-shift-${shiftType}`} className={`border ${shiftBadge.cls}`}>
                          {shiftBadge.label}
                        </Badge>
                      ) : !m.has_pin ? (
                        <Badge className="border border-amber-400/40 bg-amber-400/10 text-amber-200">No PIN</Badge>
                      ) : m.clocked_in ? (
                        <Badge data-testid="kiosk-staff-onshift" className="border border-sky-400/40 bg-sky-400/10 text-sky-200">
                          <Clock className="mr-1 h-3 w-3" /> On shift
                        </Badge>
                      ) : (
                        <Badge className="border border-emerald-400/40 bg-emerald-400/10 text-emerald-200">Tap</Badge>
                      )}
                    </button>
                  );
                })}
              </div>
            )}

            <p className="mt-6 text-center text-xs opacity-50">
              No account? Ask an admin to set up your kiosk PIN.
            </p>
          </>
        )}
      </main>
    </div>
  );
}
